import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  Observable,
  catchError,
  of,
  tap,
  switchMap,
  BehaviorSubject,
  map,
} from 'rxjs';
import { ServicecallService, GeneralService } from 'src/app';
import {
  PlanogramEntity,
  PlanogramSummaryItem,
  PlanogramTis,
  PlanogramPayload,
  TimelapseData,
} from 'src/app/model';

@Injectable({
  providedIn: 'root',
})
export class PlanogramSharedService {
  // Public observables for components to subscribe to
  private _planogramSummarySubject = new BehaviorSubject<
    PlanogramSummaryItem[]
  >([]);
  private _planogramEntitiesSubject = new BehaviorSubject<PlanogramEntity[]>(
    []
  );
  private _planogramEntityListSubject = new BehaviorSubject<any[]>([]);
  private _filterFieldsSubject = new BehaviorSubject<any>({});

  // Exposed observables
  public planogramSummary$ = this._planogramSummarySubject.asObservable();
  public planogramEntities$ = this._planogramEntitiesSubject.asObservable();
  public planogramEntityList$ = this._planogramEntityListSubject.asObservable();
  public filterFields$ = this._filterFieldsSubject.asObservable();

  // Service state
  private _statusMapping: Record<string, string> = {};
  private _processedSummaryData: PlanogramSummaryItem[] = [];
  private _currentZone: any = null;
  private _currentFilters: { [key: string]: { [key: string]: boolean } } = {};
  private _categoryList: { [key: string]: string | undefined } = {};

  // Color mappings
  public summaryColorMapping: Record<number | string, string> = {};
  public backgroundColorMapping: Record<number | string, string> = {};
  public historyBackgroundColorMapping: Record<number | string, string> = {};

  constructor(
    private _service: ServicecallService,
    private _datePipe: DatePipe,
    private _generalService: GeneralService
  ) {
    this._setConfiguration();
  }

  /**
   * Initialize data for a specific zone with filters
   * @param zone - The zone plan model
   * @param filters - Optional filter values
   */
  public initialize(
    zone: any,
    isLoaderNeeded: boolean,
    filters?: { [key: string]: { [key: string]: boolean } }
  ): void {
    this._currentZone = zone;

    if (filters) {
      this._currentFilters = { ...filters };
    }

    this._getPlanogramSummary();
    this._getPlanogramEntities(isLoaderNeeded);
  }

  /**
   * Sets configuration for filters and color mappings
   */
  private _setConfiguration(): void {
    const filterFields =
      this._service.getConfiguration('entityFilter')?.filterPlanogramFields ??
      {};
    this._categoryList =
      this._service.getConfiguration('tenantGenericConfig')?.entityCategories ??
      {};
    // Validate and map status field values
    this._statusMapping =
      filterFields?.['status']?.fieldValues?.reduce(
        (acc: Record<string, string>, item: any) => {
          acc[item.value] = item.name;
          return acc;
        },
        {}
      ) || {};

    this.summaryColorMapping = this._generalService.createColorMapping(
      filterFields?.[`status`]?.fieldValues
    );

    this.backgroundColorMapping = this._generalService.createColorMapping(
      filterFields?.[`status`]?.fieldValues,
      'bgTrackState'
    );

    this.historyBackgroundColorMapping =
      this._generalService.createColorMapping(
        filterFields?.[`status`]?.fieldValues,
        'historyState'
      );

    this._filterFieldsSubject.next(filterFields);
  }

  /**
   * Update filters
   * @param filters - New filter values
   */
  public updateFilters(filters: {
    [key: string]: { [key: string]: boolean };
  }): void {
    this._currentFilters = { ...filters };
    this.initialize(this._currentZone, false);
  }

  /**
   * Extract array of values from filter structure
   * @param filterKey - Key of the filter in the filters object
   * @returns Array of selected values
   */
  private _extractFilterValues(filterKey: string): string[] {
    const filterData = this._currentFilters[filterKey];
    if (!filterData) return [];
    if (filterKey === 'searchKey') {
      if (typeof filterData === 'string') {
        return [filterData];
      } else if (Array.isArray(filterData)) {
        return filterData;
      } else {
        return Object.keys(filterData).filter(
          (key) => filterData[key] === true
        );
      }
    } else {
      // Check the type and handle accordingly
      if (Array.isArray(filterData)) {
        return filterData; // If it's already an array, return it
      } else if (typeof filterData === 'object') {
        // If it's an object, convert it to an array of keys where value is true
        return Object.keys(filterData).filter(
          (key) => filterData[key] === true
        );
      } else if (typeof filterData === 'string') {
        return [filterData];
      }

      return [];
    }
  }

  /**
   * Generates the API request payload based on the request type.
   * @param requestType - The type of API request.
   * @param entityIds - (Optional) Entity IDs required for the tracker API.
   * @returns The generated payload for the specific API request.
   */
  private _payloadGenerator(
    requestType:
      | 'planogramSummary'
      | 'planogramEntities'
      | 'trackerTIS'
      | 'planogramTimelapse'
      | 'TISHistory',
    entityIds: string[] = []
  ): Record<string, any> {
    const selectedDate = this._generalService?.getPageControlDateSelection();
    // Get dynamic values from filters - properly extracted as arrays
    const category =
      this._extractFilterValues('category')[0] ||
      this._categoryList?.[`workBench`];
    const worktableZoneIds = this._extractFilterValues('workTable');
    const trackedState = this._extractFilterValues('status');
    const searchKey = this._extractFilterValues('searchKey')[0] || '';

    const basePayload: PlanogramPayload = {
      from: this._datePipe.transform(
        selectedDate?.startDate,
        'yyyy-MM-ddTHH:mm:ssZZ'
      ),
      to: this._datePipe.transform(
        selectedDate?.endDate,
        'yyyy-MM-ddTHH:mm:ssZZ'
      ),
      // zoneId: this._currentZone?._id,
      hierarchyId: this._currentZone?._id,
      siteId: sessionStorage.getItem('siteId')
        ? JSON.parse(sessionStorage.getItem('siteId') || '{}')
        : null,
      category,
      worktableZoneIds,
    };
    if (requestType !== 'planogramTimelapse') {
      basePayload.trackedState = trackedState;
    }

    switch (requestType) {
      case 'planogramSummary':
        return { ...basePayload, entityIds };

      case 'planogramEntities':
        return { ...basePayload, searchKey };

      case 'trackerTIS':
        return { from: basePayload.from, to: basePayload.to, entityIds };

      case 'planogramTimelapse':
        return { ...basePayload, entityIds };

      case 'TISHistory':
        return {
          from: basePayload.from,
          to: basePayload.to,
          entityId: entityIds?.[0],
        };

      default:
        throw new Error(`Invalid request type: ${requestType}`);
    }
  }

  /**
   * Fetches the planogram summary details.
   */
  private _getPlanogramSummary(): void {
    try {
      const payload = this._payloadGenerator('planogramSummary');

      this._service._getPlanogramSummary(payload).subscribe({
        next: (result: PlanogramSummaryItem[]) => {
          if (!Array.isArray(result)) {
            console.error('Invalid response format:', result);
            return;
          }

          // Transform API response
          const transformedData: PlanogramSummaryItem[] = result.map(
            (item: PlanogramSummaryItem) => ({
              ...item,
              name: this._statusMapping[item.trackedState] || 'Unknown',
            })
          );

          // Calculate total count
          const totalCount = transformedData.reduce(
            (sum: number, item: PlanogramSummaryItem) =>
              sum + (item.count || 0),
            0
          );

          // Ensure all status types are represented
          const filterFields = this._filterFieldsSubject.getValue();
          const allStatusValues =
            filterFields?.[`status`]?.fieldValues?.map((f: any) => f.value) ||
            [];

          const summaryMap = new Map(
            transformedData.map((item) => [item.trackedState, item.count || 0])
          );

          // Create processed summary data
          this._processedSummaryData = [
            { trackedState: null, count: totalCount, name: 'Total' },
            ...allStatusValues.map((value) => ({
              trackedState: value,
              name: this._statusMapping[value] || 'Unknown',
              count: summaryMap.get(value) || 0,
            })),
          ];

          // Update the subject with the new data
          this._planogramSummarySubject.next(this._processedSummaryData);
        },
        error: (err: any) => {
          console.error('Error fetching planogram summary:', err);
          this._planogramSummarySubject.next([]);
        },
      });
    } catch (error) {
      console.error('Unexpected error in _getPlanogramSummary:', error);
      this._planogramSummarySubject.next([]);
    }
  }

  /**
   * Fetches the planogram entities.
   */
  private _getPlanogramEntities(isLoaderNeeded: boolean): void {
    const payload = this._payloadGenerator('planogramEntities');

    this._service
      .getFloorPlanEntities(payload, isLoaderNeeded)
      .pipe(
        tap((result: PlanogramEntity[]) => {
          if (!Array.isArray(result)) {
            throw new Error('Invalid response format');
          }
          // Update entities subject
          this._planogramEntitiesSubject.next(result);
          // Extract unique workTableZone data and update filterFields
          const uniqueWorkTableZones = Array.from(
            new Map(
              result
                .map((item) => item?.workTableZone?.[0]) // Get first workTableZone if it exists
                .filter((zone) => zone && zone._id && zone.name) // Filter out invalid entries
                .map((zone) => [zone._id, zone]) // Create map entries with _id as key
            ).values() // Get unique values based on _id
          );
          const currentFilterFields = this._filterFieldsSubject.getValue();
          const updatedFilterFields = {
            ...currentFilterFields,
            workTable: {
              ...currentFilterFields[`workTable`],
              fieldValues: uniqueWorkTableZones.map(
                (zone: { _id: string; name: string }) => ({
                  name: zone?.name ?? 'NA',
                  value: zone?._id ?? 'NA',
                })
              ),
            },
          };

          this._filterFieldsSubject.next(updatedFilterFields);
        }),
        switchMap((result: PlanogramEntity[]) => {
          const entityIds = result.map((item) => item?.entityId);
          return entityIds.length > 0
            ? this._getPlanogramTis(entityIds)
            : of([]);
        }),
        tap((tisResults: PlanogramTis[]) => {
          // Get current entities
          const entities = this._planogramEntitiesSubject.getValue();
          // Update entities with TIS information
          if (tisResults && tisResults.length > 0) {
            const updatedEntities = entities?.map((entity) => {
              // Find corresponding TIS entry
              const tisSummary = tisResults.find(
                (tis) => tis?.entityId === entity?.entityId
              );
              const selectedDate =
                this._generalService?.getPageControlDateSelection();
              if (tisSummary) {
                // Add TIS key to the entity
                return {
                  ...entity,
                  tis: tisSummary.TIS,
                  sourceTimeDisplayValue:
                    this._generalService.getHumanReadableTimeDifference(
                      new Date().toISOString(),
                      entity?.sourceTimestamp
                    ),
                  // Generate human-readable TIS display value
                  tisDisplayValue:
                    this._generalService.getHumanReadableTimeDifference(
                      this._datePipe.transform(
                        selectedDate?.endDate,
                        'yyyy-MM-ddTHH:mm:ssZZ'
                      ),
                      tisSummary?.TIS
                    ),
                };
              }
              return entity;
            });
            // Update entity list subject
            this._planogramEntityListSubject.next(updatedEntities);
          } else {
            this._planogramEntityListSubject.next(entities);
          }
        }),
        // Add the timelapse data after TIS
        switchMap((result) => {
          const entityIds = this._planogramEntityListSubject
            .getValue()
            .map((item) => item?.entityId);
          return entityIds.length > 0
            ? this._getPlanogramTimelapse(entityIds)
            : of([]);
        }),
        catchError((error) => {
          console.error(
            'Error fetching planogram entities or related data:',
            error
          );
          this._planogramEntityListSubject.next([]);
          return of([]);
        })
      )
      .subscribe();
  }

  /**
   * Fetches TIS data for the given entity IDs.
   * @param entityIds - The list of entity IDs obtained from the first API call.
   * @returns An Observable that emits the tracker TIS data.
   */
  private _getPlanogramTis(entityIds: string[]): Observable<PlanogramTis[]> {
    const payload = this._payloadGenerator('trackerTIS', entityIds);
    return this._service._getPlanogramTis(payload).pipe(
      catchError((error) => {
        console.error('Error fetching tracker TIS data:', error);
        return of([]);
      })
    );
  }

  /**
   * Fetches timelapse data for the given entity IDs.
   * @param entityIds - The list of entity IDs to get timelapse data for.
   * @returns An Observable that emits the timelapse data.
   */
  private _getPlanogramTimelapse(entityIds: string[]): Observable<any[]> {
    const payload = this._payloadGenerator('planogramTimelapse', entityIds);

    return this._service._getPlanogramTimelapse(payload).pipe(
      tap((timelapseResults: any[]) => {
        // Get current entity list
        const entityList = this._planogramEntityListSubject.getValue();

        // Update entity list with timelapse information
        if (timelapseResults && timelapseResults.length > 0) {
          const updatedEntityList = entityList?.map((entity) => {
            // Find corresponding timelapse entry
            const timelapseData = timelapseResults.find(
              (item) => item?.entityId === entity?.entityId
            );

            if (timelapseData) {
              // Extract status values and convert to timelapse array format
              const timelapseArray = Object.entries(timelapseData)
                .filter(([key]) => key.match(/^-?\d+(\.\d+)?$/)) // Match positive/negative integers and decimals
                .map(([key, value]) => ({
                  trackedState: key,
                  value: value.toString(),
                }))
                // Sort in descending order based on time values
                .sort((a, b) => parseInt(b.value, 10) - parseInt(a.value, 10));

              // Calculate total seconds across all states
              const timelapseTotal = timelapseArray.reduce(
                (sum, segment) => sum + parseInt(segment.value, 10),
                0
              );

              // Add processed timelapse data to the entity
              return {
                ...entity,
                timelapse: timelapseArray,
                timelapseTotal: timelapseTotal,
              };
            }

            return entity;
          });

          // Update the entity list subject
          this._planogramEntityListSubject.next(updatedEntityList);
        }
      }),
      catchError((error) => {
        console.error('Error fetching timelapse data:', error);
        return of([]);
      })
    );
  }

  /**
   * Gets the status name for the given tracked state
   * @param trackedState - The tracked state value
   * @returns The name of the status from planogramSummaryData
   */
  public getStatusName(trackedState: string | null): string {
    if (!trackedState) {
      return 'NA';
    }

    const summaryItem = this._processedSummaryData.find(
      (item) => item.trackedState === trackedState
    );

    return summaryItem?.name || 'NA';
  }

  /**
   * Calculate the width percentage for a timelapse segment
   * @param value - Segment value
   * @param total - Total value of all segments
   * @returns Percentage width as string with % suffix
   */
  public calculateSegmentWidth(value: string, total: number): string {
    if (!total || total <= 0) return '0%';
    const percentage = (parseFloat(value) / total) * 100;
    return `${percentage.toFixed(1)}%`;
  }

  /**
   * Fetches historical data for a specific entity and removes consecutive duplicate trackedState entries
   * @param entityId - The ID of the entity to fetch history for
   * @returns Observable of filtered history data
   */
  public getEntityHistory(entityId: string): Observable<any[]> {
    const payload = this._payloadGenerator('TISHistory', [entityId]);

    return this._service._getPlanogramEntityHistory(payload).pipe(
      map((timelapseResults: any[]) => {
        const transformedResults =
          timelapseResults?.map((result) => ({
            ...result,
            TIS: this._datePipe.transform(result.TIS, 'MMM d, yyyy h:mm a'),
          })) || [];
        // Then filter out consecutive duplicate trackedState entries
        return transformedResults.reduce(
          (filteredResults, currentItem, index) => {
            if (index === 0) {
              return [currentItem];
            }
            const previousItem = filteredResults[filteredResults.length - 1];
            if (currentItem.trackedState !== previousItem.trackedState) {
              filteredResults.push(currentItem);
            }
            return filteredResults;
          },
          []
        );
      }),
      catchError((error) => {
        console.error('Error fetching history data:', error);
        return of([]);
      })
    );
  }
}
